import { useState } from "react";
import { InstagramLogo, TiktokLogo, FacebookLogo, XLogo, WhatsappLogo, LinkedinLogo, EnvelopeSimple, Link as LinkIcon, Check, ShareNetwork } from "@phosphor-icons/react";
import { trackConversion } from "@/lib/analyticsClient";

/**
 * ShareBar — social share buttons for the Sweat for Sacrifice campaign.
 * Instagram + TikTok don't accept URL sharing via web intents, so we copy
 * the caption + link and instruct the user to paste into their app.
 */
export default function ShareBar() {
  const url = typeof window !== "undefined"
    ? `${window.location.origin}/sweat-for-sacrifice`
    : "https://sweatwithslay.com/sweat-for-sacrifice";
  const caption = "911 minutes of movement in September. In honor of the fallen of 9/11, my uncles Timmy + Harry, and every family carrying a name. Every dollar to Tunnel to Towers + FDNY Fire Family Transport. Every name lives on the Legacy Wall — forever.\n\nWho are you moving for?";
  const hashtags = "#SweatForSacrifice #NeverForget911 #TunnelToTowers #FDNY #SweatWithSlay";
  const shareText = `${caption}\n\n${hashtags}\n\n${url}`;

  const [copied, setCopied] = useState("");

  /** Fire a share_click conversion so the Admin dashboard tallies which
   *  platform earns the most amplification. Fully anonymous. */
  const emitShare = (platform) => {
    try { trackConversion("share_click", { refId: platform }); } catch { /* silent */ }
  };

  const doCopy = async (label, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      emitShare(label);
      setTimeout(() => setCopied(""), 2200);
    } catch (err) {
      console.warn("Share: clipboard failed", err);
    }
  };

  const openIn = (href, platform) => {
    if (platform) emitShare(platform);
    const w = window.open(href, "_blank", "noopener,noreferrer,width=650,height=650");
    if (!w) {
      // popup blocked — fallback: navigate in same tab
      window.location.href = href;
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Sweat for Sacrifice", text: caption, url });
        emitShare("native");
        return true;
      } catch (err) {
        if (err && err.name !== "AbortError") console.warn("Share: native failed", err);
      }
    }
    return false;
  };

  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(shareText);
  const encodedCaption = encodeURIComponent(`${caption}\n\n${hashtags}`);
  const encodedTwitter = encodeURIComponent(`${caption.slice(0, 200)}…\n\n${hashtags}`);

  const btnBase = "group relative inline-flex flex-col items-center justify-center gap-2 border-2 border-white/20 hover:border-[#B32027] hover:bg-[#B32027]/10 bg-black/40 p-4 md:p-5 min-w-[96px] transition-all";

  return (
    <section
      data-testid="share-bar-section"
      className="px-4 md:px-8 py-16 md:py-20 max-w-5xl mx-auto border-t-2 border-[#B32027]/30"
    >
      <div className="text-center mb-8">
        <p className="text-[#B32027] uppercase tracking-[0.5em] text-[11px] font-bold mb-4">
          / Amplify · share the mission
        </p>
        <h2 className="font-display font-black uppercase tracking-tighter leading-[0.85] text-4xl md:text-5xl lg:text-6xl mb-4">
          Share the <span className="text-[#B32027]">tribute.</span>
        </h2>
        <p className="text-white/75 text-base md:text-lg max-w-2xl mx-auto">
          One tap to send this to a friend, a family group, or your feed. Every share puts another name on the Wall.
        </p>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        {/* Native share (mobile) */}
        <button
          type="button"
          data-testid="share-native"
          onClick={nativeShare}
          className={btnBase}
          aria-label="Share via device"
        >
          <ShareNetwork size={26} weight="fill" className="text-white group-hover:text-[#B32027]" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/80">Share</span>
        </button>

        {/* Instagram — copy caption + link (IG has no web share intent) */}
        <button
          type="button"
          data-testid="share-instagram"
          onClick={() => doCopy("instagram", `${caption}\n\n${hashtags}\n\n${url}`)}
          className={btnBase}
          aria-label="Copy for Instagram"
        >
          <InstagramLogo size={26} weight="fill" className="text-white group-hover:text-[#B32027]" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/80">
            {copied === "instagram" ? "Copied ✓" : "Instagram"}
          </span>
        </button>

        {/* TikTok — copy caption + link */}
        <button
          type="button"
          data-testid="share-tiktok"
          onClick={() => doCopy("tiktok", `${caption}\n\n${hashtags}\n\n${url}`)}
          className={btnBase}
          aria-label="Copy for TikTok"
        >
          <TiktokLogo size={26} weight="fill" className="text-white group-hover:text-[#B32027]" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/80">
            {copied === "tiktok" ? "Copied ✓" : "TikTok"}
          </span>
        </button>

        {/* Facebook */}
        <button
          type="button"
          data-testid="share-facebook"
          onClick={() => openIn(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedCaption}`, "facebook")}
          className={btnBase}
          aria-label="Share on Facebook"
        >
          <FacebookLogo size={26} weight="fill" className="text-white group-hover:text-[#B32027]" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/80">Facebook</span>
        </button>

        {/* X / Twitter */}
        <button
          type="button"
          data-testid="share-twitter"
          onClick={() => openIn(`https://twitter.com/intent/tweet?text=${encodedTwitter}&url=${encodedUrl}`, "twitter")}
          className={btnBase}
          aria-label="Share on X"
        >
          <XLogo size={26} weight="fill" className="text-white group-hover:text-[#B32027]" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/80">X / Twitter</span>
        </button>

        {/* LinkedIn */}
        <button
          type="button"
          data-testid="share-linkedin"
          onClick={() => openIn(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, "linkedin")}
          className={btnBase}
          aria-label="Share on LinkedIn"
        >
          <LinkedinLogo size={26} weight="fill" className="text-white group-hover:text-[#B32027]" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/80">LinkedIn</span>
        </button>

        {/* WhatsApp */}
        <button
          type="button"
          data-testid="share-whatsapp"
          onClick={() => openIn(`https://wa.me/?text=${encodedText}`, "whatsapp")}
          className={btnBase}
          aria-label="Share on WhatsApp"
        >
          <WhatsappLogo size={26} weight="fill" className="text-white group-hover:text-[#B32027]" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/80">WhatsApp</span>
        </button>

        {/* Email */}
        <button
          type="button"
          data-testid="share-email"
          onClick={() => openIn(`mailto:?subject=${encodeURIComponent("Sweat for Sacrifice — join us this September")}&body=${encodedText}`, "email")}
          className={btnBase}
          aria-label="Share via email"
        >
          <EnvelopeSimple size={26} weight="fill" className="text-white group-hover:text-[#B32027]" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/80">Email</span>
        </button>
      </div>

      {/* Copy link + copy caption */}
      <div className="mt-6 grid sm:grid-cols-2 gap-3 max-w-3xl mx-auto">
        <button
          type="button"
          data-testid="share-copy-link"
          onClick={() => doCopy("link", url)}
          className={`inline-flex items-center justify-center gap-3 border-2 ${copied === "link" ? "border-[#B32027] bg-[#B32027] text-white" : "border-white/20 hover:border-[#B32027] text-white/85"} px-5 py-4 text-[11px] font-bold uppercase tracking-[0.25em] transition-all`}
        >
          {copied === "link" ? <><Check size={14} weight="bold" /> Link copied</> : <><LinkIcon size={14} weight="bold" /> Copy link</>}
        </button>
        <button
          type="button"
          data-testid="share-copy-caption"
          onClick={() => doCopy("caption", `${caption}\n\n${hashtags}\n\n${url}`)}
          className={`inline-flex items-center justify-center gap-3 border-2 ${copied === "caption" ? "border-[#B32027] bg-[#B32027] text-white" : "border-white/20 hover:border-[#B32027] text-white/85"} px-5 py-4 text-[11px] font-bold uppercase tracking-[0.25em] transition-all`}
        >
          {copied === "caption" ? <><Check size={14} weight="bold" /> Caption copied</> : <><ShareNetwork size={14} weight="bold" /> Copy caption + hashtags</>}
        </button>
      </div>

      <p className="mt-8 text-center text-white/45 text-[10px] uppercase tracking-[0.35em] font-bold">
        Instagram &amp; TikTok · copy captions, then paste into a new post or story
      </p>
    </section>
  );
}
